const submitButtonHiddenFieldId = '__submit__field__';

export class ValidationInstance {
    constructor(form, validation, options) {
        this._form = form;
        this._validation = validation;
        this._options = Object.assign({
            groupSelector: 'form-group',
            inputSelector: '[name]',
            onValidationError: () => {},
            onValidationSuccess: () => {},
            onClearValidationErrors: () => {},
            onSubmit: () => {},
        }, options ? options : {});
        this.validate = this.validate.bind(this);
        this._validate = this._validate.bind(this);
        this._init = this._init.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this.getInput = this.getInput.bind(this);
        this.getInputs = this.getInputs.bind(this);

        this._init();
    }

    get form() {
        return this._form;
    }

    get inputGroups() {
        const selector = this._options.groupSelector ? this._options.groupSelector : 'form-group';
        return Array.from(this._form.querySelectorAll(selector)).filter(group => this.getInput(group) !== null);
    }

    /**
     * @param {HTMLElement} fieldGroup
     * @returns {HTMLInputElement|null}
     */
    getInput(fieldGroup) {
        return this.getInputs(fieldGroup)[0] || null;
    }

    /**
     * @param {HTMLElement} fieldGroup
     * @return {HTMLInputElement[]}
     */
    getInputs(fieldGroup) {
        const result = [];
        let inputs = fieldGroup.querySelectorAll(this._options.inputSelector);
        for (let i = 0; i < inputs.length; i++) {
            let input = inputs[i];
            if (input.getAttribute('type') === 'hidden') {
                const sibling = input.nextElementSibling;
                if (sibling && sibling.matches(this._options.inputSelector) && sibling.getAttribute('type') === 'checkbox') {
                    input = sibling;
                    i++;
                }
            }
            result.push(input);
        }
        return result;
    }

    /**
     * @param {HTMLElement} inputGroup
     * @returns {string}
     */
    static getLabel(inputGroup) {
        let label = inputGroup.querySelector('label');
        let validationLabel = label.getAttribute('data-validation-label');
        if (validationLabel === null) {
            const fieldset = inputGroup.closest('fieldset');
            if (fieldset !== null) {
                const legend = fieldset.querySelector('legend');
                if (legend !== null) {
                    label = legend;
                }
            }
            const cbLabel = label.querySelector('.form-check-label__text');
            if (cbLabel) {
                label = cbLabel;
            } else if (label.querySelector('span')) {
                label = label.querySelector('span');
            }
            validationLabel = label.innerText;
        }
        return validationLabel;
    }

    /**
     * @param {HTMLInputElement} input
     * @returns {string}
     */
    static getIdentifier(input) {
        let id = input.id;
        if (input.getAttribute('type') === 'radio' || input.getAttribute('type') === 'checkbox') {
            id = id.split('-').slice(0, -1).join('-');
        }
        return id;
    }

    validate(inputGroups) {
        let inputs;
        if (inputGroups) {
            inputs = inputGroups;
        } else {
            inputs = this.inputGroups;
        }
        return Array.from(inputs).reduce((p, group) => this._validate(group)() && p, true);
    }

    _validate(inputGroup) {
        return () => {
            const input = this.getInput(inputGroup);
            if (!input || input.hidden) {
                return;
            }
            let valid = true;
            const validators = this._validation.validators;
            Object.keys(validators).forEach(validatorName => {
                const validator = validators[validatorName];
                if (validator.isApplicable(input, this)) {
                    validator.validate(input, this)(
                        (args) => {
                            valid = false;
                            this._options.onValidationError(inputGroup, validatorName, args);
                        },
                        (args) => {
                            this._options.onValidationSuccess(inputGroup, validatorName, args);
                        });
                }
            });
            return valid;
        };
    }

    /**
     * Mirrors name and value form the given submit button into a hidden field
     * @param {HTMLInputElement|HTMLButtonElement} button
     * @private
     */
    _createHiddenFieldFromSubmitButton(button) {
        const field = document.createElement('INPUT');
        field.type = 'hidden';
        field.name = button.name;
        field.value = button.value;
        field.id = submitButtonHiddenFieldId;
        this._form.appendChild(field);
    }

    /**
     * @param {MouseEvent} e
     * @private
     */
    _onSubmit(e) {
        const hiddenField = this._form.querySelector(`#${submitButtonHiddenFieldId}`);
        if (hiddenField) {
            hiddenField.remove();
        }
        e.preventDefault();
        this.clearValidationErrors();
        const result = this.validate();
        this._options.onSubmit(result);
        if (result) {
            if (e.target instanceof HTMLButtonElement || e.target instanceof HTMLInputElement) {
                this._createHiddenFieldFromSubmitButton(e.target);
            }
            this._form.submit();
        }
    }

    _init() {
        Array.from(this.inputGroups).forEach(group => {
            const inputs = this.getInputs(group);
            inputs.forEach(input => input.addEventListener('change', this._validate(group)));
        });
        this._form.setAttribute('novalidate', '');
        Array.from(this._form.querySelectorAll('[type=submit]')).forEach(button =>
            button.addEventListener('click', this._onSubmit));
    }

    clearValidationErrors() {
        this._options.onClearValidationErrors();
    }
}
